"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.closeOutJobSchema = exports.CLOSURE_POOL_CHANGES_MAX = exports.CLOSE_REASON_MIN = exports.PAST_SCREENING_STAGES = exports.INTERVIEW_REACHED_STAGES = exports.POSITION_FILLED_REASON_CODE = exports.UNSENT_OFFER_STATUSES = exports.LIVE_OFFER_STATUSES = exports.recordExitSchema = exports.PERSON_NAME_MAX = exports.EXIT_TYPE_LABELS = exports.EXIT_TYPES = exports.confirmJoiningSchema = exports.employeeCodeSchema = exports.EMPLOYEE_CODE_MAX = exports.reviewDocumentSchema = exports.CANDIDATE_DOCUMENT_STATE_LABELS = exports.JOINING_DOCUMENT_STATE_LABELS = exports.JOINING_DOCUMENT_STATES = exports.joiningDocumentsSchema = exports.joiningDocumentSchema = exports.JOINING_DOCUMENT_STARTERS = exports.JOINING_DOCUMENT_VALID_UNTIL_FIELD = exports.JOINING_DOCUMENT_EXTENSIONS = exports.JOINING_DOCUMENT_MAX_BYTES = exports.DOCUMENT_REJECTION_MAX = exports.DOCUMENT_REJECTION_MIN = exports.JOINING_DOCUMENT_LABEL_MAX = exports.JOINING_DOCUMENTS_MAX = exports.JOINING_DOCUMENT_PREFIX = void 0;
exports.defaultJoiningDocuments = defaultJoiningDocuments;
exports.joiningDocumentsOf = joiningDocumentsOf;
exports.joiningDocumentType = joiningDocumentType;
exports.joiningKeyOf = joiningKeyOf;
exports.documentState = documentState;
exports.outstandingDocuments = outstandingDocuments;
exports.isDocumentExpired = isDocumentExpired;
exports.joinedOnProblem = joinedOnProblem;
exports.timeToSlotDays = timeToSlotDays;
exports.isSeatDueToEmpty = isSeatDueToEmpty;
exports.isCloseReasonRequired = isCloseReasonRequired;
const zod_1 = require("zod");
const calendars_1 = require("./calendars");
const offers_1 = require("./offers");
const publishing_1 = require("./publishing");
const schemas_1 = require("./schemas");
// S24 — hire conversion, closure and backfill (OFR-04–06, F13.12–F13.17, F6.06).
// Sources: master plan S24 and its planning decisions (DECISIONS-LOG S24 Q1–Q8); E1; E11;
// E20; D2; Appendix A and B; §L11; §L13; §L15; §L18; §L19; §L22 N-56, N-57, N-59; §L23; §L25.
// --- Joining documents (§L11, S24 Q1, Q2) --------------------------------------------------------
/** A document's `document_type` is this prefix and the job's key for it. */
exports.JOINING_DOCUMENT_PREFIX = 'joining:';
exports.JOINING_DOCUMENTS_MAX = 20;
exports.JOINING_DOCUMENT_LABEL_MAX = 80;
/** §L11: a rejection needs a reason, which the candidate is told. */
exports.DOCUMENT_REJECTION_MIN = 3;
exports.DOCUMENT_REJECTION_MAX = 500;
/** §L26 "Certificates, documents": PDF, JPG or PNG, 5 MB each. */
exports.JOINING_DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;
exports.JOINING_DOCUMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
/** The candidate's upload travels with the offer link's header (as ADR 0019). */
exports.JOINING_DOCUMENT_VALID_UNTIL_FIELD = 'validUntil';
/**
 * S24 Q2: the starter list step 7 ticks from. A new job asks for all six; the four a hire
 * cannot start without are required. The recruiter unticks or adds their own.
 */
exports.JOINING_DOCUMENT_STARTERS = [
    { key: 'nid', label: 'NID or passport', isRequired: true },
    { key: 'photo', label: 'Photo', isRequired: true },
    { key: 'education', label: 'Education certificates', isRequired: true },
    { key: 'release_letter', label: 'Release or experience letter', isRequired: false },
    { key: 'bank', label: 'Bank account details', isRequired: true },
    { key: 'tin', label: 'TIN certificate', isRequired: false },
];
exports.joiningDocumentSchema = zod_1.z.object({
    key: zod_1.z.string().regex(/^[a-z0-9_-]{1,40}$/, 'Document keys are lower-case letters, numbers, dashes and underscores.'),
    label: zod_1.z.string().trim().min(2, 'Name the document.').max(exports.JOINING_DOCUMENT_LABEL_MAX),
    isRequired: zod_1.z.boolean(),
});
exports.joiningDocumentsSchema = zod_1.z
    .array(exports.joiningDocumentSchema)
    .max(exports.JOINING_DOCUMENTS_MAX, `At most ${exports.JOINING_DOCUMENTS_MAX} documents.`)
    .superRefine((documents, ctx) => {
    const keys = new Set();
    const labels = new Set();
    documents.forEach((document, index) => {
        if (keys.has(document.key))
            ctx.addIssue({ code: 'custom', message: 'Each document needs its own key.', path: [index, 'key'] });
        const label = document.label.trim().toLowerCase();
        if (labels.has(label))
            ctx.addIssue({ code: 'custom', message: 'This document is already on the list.', path: [index, 'label'] });
        keys.add(document.key);
        labels.add(label);
    });
});
function defaultJoiningDocuments() {
    return exports.JOINING_DOCUMENT_STARTERS.map((starter) => ({ ...starter }));
}
/** A job's stored list; one that doesn't read as a list counts as none rather than as the starters. */
function joiningDocumentsOf(value) {
    const parsed = exports.joiningDocumentsSchema.safeParse(value);
    return parsed.success ? parsed.data : [];
}
function joiningDocumentType(key) {
    return `${exports.JOINING_DOCUMENT_PREFIX}${key}`;
}
/** The job's key for a stored document, or null when it isn't a joining document. */
function joiningKeyOf(documentType) {
    return documentType.startsWith(exports.JOINING_DOCUMENT_PREFIX) ? documentType.slice(exports.JOINING_DOCUMENT_PREFIX.length) : null;
}
/** §L11's states, plus `missing` for a document nobody has uploaded yet. */
exports.JOINING_DOCUMENT_STATES = ['missing', 'uploaded', 'under_review', 'verified', 'rejected', 'expired'];
exports.JOINING_DOCUMENT_STATE_LABELS = {
    missing: 'Not uploaded',
    uploaded: 'Uploaded',
    under_review: 'Under review',
    verified: 'Verified',
    rejected: 'Rejected — waiting for a new one',
    expired: 'Expired',
};
/** The candidate's words (§L21) — never "under review" by someone they cannot see. */
exports.CANDIDATE_DOCUMENT_STATE_LABELS = {
    missing: 'Needed',
    uploaded: 'Received',
    under_review: 'Received',
    verified: 'Checked',
    rejected: 'Please upload again',
    expired: 'Expired — please upload a current one',
};
/** A stored document's state, as the rest of the system reads it. */
function documentState(status) {
    if (status === 'under_review' || status === 'verified' || status === 'rejected' || status === 'expired')
        return status;
    return 'uploaded';
}
/**
 * §L11's gate: every required document must be verified. Returns what is outstanding, in
 * the words the hire button shows ("NID or passport — not uploaded"). Empty when joining
 * may be confirmed.
 */
function outstandingDocuments(documents, states) {
    return documents
        .filter((document) => document.isRequired)
        .map((document) => ({ document, state: states.get(document.key) ?? 'missing' }))
        .filter(({ state }) => state !== 'verified')
        .map(({ document, state }) => `${document.label} — ${exports.JOINING_DOCUMENT_STATE_LABELS[state].toLowerCase()}`);
}
/** A "valid until" date that has passed makes the document `expired` (§L11). */
function isDocumentExpired(validUntil, today) {
    return validUntil !== null && validUntil < today;
}
exports.reviewDocumentSchema = zod_1.z.discriminatedUnion('decision', [
    zod_1.z.object({ decision: zod_1.z.literal('verified'), version: zod_1.z.number().int().nonnegative() }),
    zod_1.z.object({
        decision: zod_1.z.literal('rejected'),
        version: zod_1.z.number().int().nonnegative(),
        reason: zod_1.z.string().trim().min(exports.DOCUMENT_REJECTION_MIN, 'Say what is wrong with it, so they can fix it.').max(exports.DOCUMENT_REJECTION_MAX),
    }),
]);
// --- Hire conversion (F13.13, OFR-04, S24 Q3, Q8) -------------------------------------------------
exports.EMPLOYEE_CODE_MAX = 30;
/** S24 Q3: typed by HR to match the existing staff system — "MNG010". Kept in capitals. */
exports.employeeCodeSchema = zod_1.z
    .string()
    .trim()
    .min(2, 'Enter the employee code.')
    .max(exports.EMPLOYEE_CODE_MAX)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]*$/, 'Letters, numbers, dots, dashes and slashes only.')
    .transform((code) => code.toUpperCase());
exports.confirmJoiningSchema = zod_1.z.object({
    /** The offer's version, as read. */
    version: zod_1.z.number().int().nonnegative(),
    joinedOn: calendars_1.isoDateSchema,
    employeeCode: exports.employeeCodeSchema,
    workEmail: zod_1.z.string().trim().email('Enter a valid email address.').max(254).nullable().default(null),
    /** S24 Q8: an internal hire's old seat, which empties on the joining day. */
    leavingPositionId: schemas_1.uuidSchema.nullable().default(null),
});
/** The joining happened: not in the future, and not before the offer was accepted. */
function joinedOnProblem(joinedOn, today, acceptedOn) {
    if (joinedOn > today)
        return "The joining date can't be in the future. Confirm it on the day they join, or after.";
    if (acceptedOn !== null && joinedOn < acceptedOn)
        return `They can't have joined before accepting the offer on ${acceptedOn}.`;
    return null;
}
/**
 * Appendix B "Time to slot": `offer.accepted` → `application.joined`, in calendar days,
 * Bangladesh dates (D13). Accepted and joined on the same day is 0.
 */
function timeToSlotDays(acceptedAt, joinedOn) {
    const accepted = Date.parse(`${(0, offers_1.businessDate)(acceptedAt)}T00:00:00Z`);
    const joined = Date.parse(`${joinedOn}T00:00:00Z`);
    return Math.round((joined - accepted) / 86_400_000);
}
// --- Exits (F13.17, OFR-06, S24 Q6, Q7) -------------------------------------------------------------
/** S24 Q7. */
exports.EXIT_TYPES = ['resignation', 'termination', 'end_of_contract', 'retirement', 'internal_move', 'other'];
exports.EXIT_TYPE_LABELS = {
    resignation: 'Resignation',
    termination: 'Termination',
    end_of_contract: 'End of contract',
    retirement: 'Retirement',
    internal_move: 'Internal move',
    other: 'Other',
};
exports.PERSON_NAME_MAX = 120;
/** S24 Q6: someone hired through Nest Recruit, or a seat and a name for staff from before. */
exports.recordExitSchema = zod_1.z.discriminatedUnion('who', [
    zod_1.z.object({
        who: zod_1.z.literal('employee'),
        employeeId: schemas_1.uuidSchema,
        lastWorkingDay: calendars_1.isoDateSchema,
        exitType: zod_1.z.enum(exports.EXIT_TYPES),
        note: zod_1.z.string().trim().max(500).optional(),
    }),
    zod_1.z.object({
        who: zod_1.z.literal('seat'),
        positionId: schemas_1.uuidSchema,
        personName: zod_1.z.string().trim().min(2, 'Enter their name.').max(exports.PERSON_NAME_MAX),
        lastWorkingDay: calendars_1.isoDateSchema,
        exitType: zod_1.z.enum(exports.EXIT_TYPES),
        note: zod_1.z.string().trim().max(500).optional(),
    }),
]);
/** The seat empties on the last working day (master plan S24 QA test 5). */
function isSeatDueToEmpty(lastWorkingDay, today) {
    return lastWorkingDay <= today;
}
// --- Closure (F13.15, F13.16, OFR-05, S24 Q5) -------------------------------------------------------
/**
 * §L13 S24 note: a job never closes while an offer on it is out with the candidate or
 * accepted. These the HR Lead settles one by one.
 */
exports.LIVE_OFFER_STATUSES = ['sent', 'viewed', 'negotiating', 'accepted'];
/** Never sent: withdrawn with the close. */
exports.UNSENT_OFFER_STATUSES = ['draft', 'in_approval', 'rejected', 'approved'];
/** §L19 GEN-FILLED (S24 Q5): what a filled job's remaining candidates become. */
exports.POSITION_FILLED_REASON_CODE = 'GEN-FILLED';
/** "Anyone who reached interview stage" (§L13) is suggested for a pool. */
exports.INTERVIEW_REACHED_STAGES = ['interview_1', 'interview_2', 'interview_3', 'hold', 'offer', 'hired_pending_join'];
/**
 * §L13 Guard: "if any application is past `screening`, cancellation requires HR Lead approval with
 * a written reason" — so a partial close needs one too while anyone still running is past it.
 */
exports.PAST_SCREENING_STAGES = ['assessment', ...exports.INTERVIEW_REACHED_STAGES];
exports.CLOSE_REASON_MIN = 3;
/** §L13: is a written reason needed to close this way, with these people still in the running? */
function isCloseReasonRequired(outcome, runningStages) {
    if (outcome === null)
        return false;
    return outcome === 'cancelled' || runningStages.some((stage) => exports.PAST_SCREENING_STAGES.includes(stage));
}
/**
 * Ticks are sent as changes to the suggestion (§L13: everyone who reached an interview), so a
 * large job's request stays small. This caps each list well above any one job's people.
 */
exports.CLOSURE_POOL_CHANGES_MAX = 5000;
exports.closeOutJobSchema = zod_1.z
    .object({
    version: zod_1.z.number().int().nonnegative(),
    /** Null on a job the last joining already filled: only its remaining candidates are left. */
    outcome: zod_1.z.enum(publishing_1.JOB_CLOSE_OUTCOMES).nullable(),
    reason: zod_1.z.string().trim().max(500).optional(),
    /** F13.16: the letter to everyone still in the running. */
    isLetterSent: zod_1.z.boolean().default(true),
    /** Ignored while Talent pools is off (#13). */
    pool: zod_1.z
        .object({
        /** Suggested for a pool, but unticked. */
        exceptIds: zod_1.z.array(schemas_1.uuidSchema).max(exports.CLOSURE_POOL_CHANGES_MAX).default([]),
        /** Not suggested, but ticked. */
        alsoIds: zod_1.z.array(schemas_1.uuidSchema).max(exports.CLOSURE_POOL_CHANGES_MAX).default([]),
        poolIds: zod_1.z.array(schemas_1.uuidSchema).max(10).default([]),
        newPoolNames: zod_1.z.array(zod_1.z.string().trim().min(2).max(80)).max(5).default([]),
    })
        .default({ exceptIds: [], alsoIds: [], poolIds: [], newPoolNames: [] }),
})
    .superRefine((input, ctx) => {
    if (input.outcome === 'cancelled' && (input.reason ?? '').length < exports.CLOSE_REASON_MIN) {
        ctx.addIssue({ code: 'custom', message: 'Say why the job is cancelled.', path: ['reason'] });
    }
});
//# sourceMappingURL=hires.js.map